import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { InputField } from '@/components/ui/input-field'
import { Label } from '@/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { useTargetedAreaWebsocket } from '@/hooks/useTargetedAreaWebsocket'
import { useTargetingCoverage } from '@/hooks/useTargetingCoverage'
import { useGridVersions } from '@/queries/grids/useGridVersions'
import { useLocations } from '@/queries/locations/useLocations'
import { useAppDispatch, useAppSelector } from '@/redux/hooks/useAppState'
import {
	commitTargetingFilter,
	gridDataSelector,
	manualExclusionFiltersSelector,
	manualInclusionFiltersSelector,
	removeTargetingFilter,
	selectedVersionIdSelector,
	setGrid,
	setSelectedVersion,
	setTargetedAreas,
	setTargetingFilters,
	targetingFiltersSelector,
	updateTargetingFilter,
	versionSelector,
} from '@/redux/store/slices/grid/gridSlice'
import {
	selectedReferenceFieldSelector,
	setSelectedReferenceField,
} from '@/redux/store/slices/map/mapSlice'
import { enabledReferenceFields, referenceFieldStringMap } from '@/utils/constants'
import { useStytchOrganization } from '@stytch/react/b2b'
import { DEFAULT_RADIUS_VALUE, NAME_CHARACTER_LIMIT, ReferenceFields } from 'honeygrid-types'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { AddTargetedAreaModal } from '../AddTargetedAreaModal'
import { CardWithSlider } from '../Card/CardWithSlider'
import { ManualFilterCard } from '../Card/ManualFilterCard'
import { GridDataDisplay } from '../GridDataDisplay'
import { TargetingFilterModal } from '../TargetingFilterModal/TargetingFilterModal'
import GridTargetedAreasSection from './GridTargetedAreasSection'

import type { County, WorkspaceLocation, TargetingType } from 'honeygrid-types'
import type { FC } from 'react'

export const GridSideControllerContainer: FC = () => {
	const { gridId = '' } = useParams()
	const { organization } = useStytchOrganization()
	const [searchParams] = useSearchParams()
	// When on the /add route, gridId param will be "add", so prioritize query param
	const gridDoId =
		gridId === 'add' ? searchParams.get('gridId') || '' : gridId || searchParams.get('gridId') || ''
	const { data: locations } = useLocations({ workspaceId: organization?.organization_id })
	const { data: honeygridData, updateTargetedAreas } = useTargetedAreaWebsocket()
	const { data: versions = [] } = useGridVersions({
		workspaceId: organization?.organization_id,
		gridId,
		enabled: !!gridId,
	})
	const [isTargetingFilterModalOpen, setIsTargetingFilterModalOpen] = useState(false)
	const [isAddTargetedAreaModalOpen, setIsAddTargetedAreaModalOpen] = useState(false)
	const grid = useAppSelector(gridDataSelector)
	const version = useAppSelector(versionSelector)
	const targetingFiltersState = useAppSelector(targetingFiltersSelector) ?? {}
	const selectedReferenceField = useAppSelector(selectedReferenceFieldSelector)
	const { coverage, targetedHouseholdCount, totalHouseholdCount } = useTargetingCoverage()
	const [counties, setCounties] = useState<County[]>([])
	const [zipCodes, setZipCodes] = useState<string[]>([])

	const manualInclusionFilters = useAppSelector(manualInclusionFiltersSelector)
	const manualExclusionFilters = useAppSelector(manualExclusionFiltersSelector)
	const selectedVersionId = useAppSelector(selectedVersionIdSelector)
	const hasInclusionFilters = Boolean(manualInclusionFilters?.length)
	const hasExclusionFilters = Boolean(manualExclusionFilters?.length)
	const hasTargetingFilters = Object.entries(targetingFiltersState).length > 0
	const hasValidTargetingAreas =
		version?.targeted_areas?.coordinates?.length ||
		version?.targeted_areas?.counties?.length ||
		version?.targeted_areas?.zipCodes?.length

	// Extract custom field data from honeygridData
	const customFieldKeys = honeygridData?.aggregatedDemographics
		? Object.keys(
				honeygridData.aggregatedDemographics.cells[
					Object.keys(honeygridData.aggregatedDemographics.cells)[0]
				] || {}
			).filter((key) => key.startsWith('ds_'))
		: []
	const customFieldMappings = honeygridData?.fieldMappings || {}

	useEffect(() => {
		import('../../data/counties.json').then((countiesJson) => {
			setCounties(countiesJson.default as any[])
		})
		import('../../data/zipCodes.json').then((zipCodesJson) => {
			setZipCodes(zipCodesJson.default as any[])
		})
	}, [])

	const dispatch = useAppDispatch()

	// Set defaults for new grids
	useEffect(() => {
		if (!gridId && grid && version && !hasValidTargetingAreas && locations?.length) {
			const defaultTargetedAreas = {
				counties: [],
				zipCodes: [],
				coordinates: [
					{
						lat: locations[0].latitude,
						lng: locations[0].longitude,
						radius: DEFAULT_RADIUS_VALUE,
						locationId: locations[0].uuid,
					},
				],
			}

			// Update Redux state
			dispatch(
				setGrid({
					...grid,
					currentVersion: {
						...version,
						targeting_type: 'radius',
						targeted_areas: defaultTargetedAreas,
					},
				})
			)

			// Proactively sync with GridDO via WebSocket
			if (gridDoId) {
				updateTargetedAreas(defaultTargetedAreas).catch(console.error)
			}
		}
	}, [
		gridId,
		grid,
		version,
		hasValidTargetingAreas,
		locations,
		dispatch,
		gridDoId,
		updateTargetedAreas,
	])

	if (!grid) {
		return null
	}

	const handleAddFilterButton = () => {
		setIsTargetingFilterModalOpen(true)
	}

	const handleRemoveTargetingFilter = (filterKey: string) => {
		dispatch(removeTargetingFilter(filterKey))
	}

	const handleAddTargetedArea = (targetedArea: {
		type: TargetingType
		data: {
			locationId?: WorkspaceLocation['uuid']
			radius?: number
			counties?: string[]
			zipCodes?: string[]
		}
	}) => {
		if (!grid || !version) return

		const newTargetedAreas = { ...version.targeted_areas }

		if (targetedArea.type === 'radius' && targetedArea.data.locationId) {
			const location = locations?.find((l) => l.uuid === targetedArea.data.locationId)
			if (location) {
				newTargetedAreas.coordinates = [
					...(newTargetedAreas.coordinates || []),
					{
						lat: location.latitude,
						lng: location.longitude,
						radius: targetedArea.data.radius || DEFAULT_RADIUS_VALUE,
						locationId: location.uuid,
					},
				]
			}
		} else if (targetedArea.type === 'county' && targetedArea.data.counties) {
			newTargetedAreas.counties = [
				...(newTargetedAreas.counties || []),
				...targetedArea.data.counties,
			]
		} else if (targetedArea.type === 'zip code' && targetedArea.data.zipCodes) {
			newTargetedAreas.zipCodes = [
				...(newTargetedAreas.zipCodes || []),
				...targetedArea.data.zipCodes,
			]
		}

		dispatch(setTargetedAreas(newTargetedAreas))

		// Update targeted areas via websocket
		if (gridDoId) {
			updateTargetedAreas(newTargetedAreas).catch(console.error)
		}
	}

	const removeTargetedArea = (type: TargetingType, id: WorkspaceLocation['uuid']) => {
		if (!grid || !version) return

		const newTargetedAreas = { ...version.targeted_areas }

		if (type === 'radius') {
			newTargetedAreas.coordinates =
				newTargetedAreas.coordinates?.filter(
					(coord: { locationId: WorkspaceLocation['uuid'] }) => coord.locationId !== id
				) || []
		} else if (type === 'county') {
			// Handle special case for removing all counties
			if (id === 'REMOVE_ALL') {
				newTargetedAreas.counties = []
			} else {
				newTargetedAreas.counties =
					newTargetedAreas.counties?.filter((countyId: string) => countyId !== id) || []
			}
		} else if (type === 'zip code') {
			newTargetedAreas.zipCodes =
				newTargetedAreas.zipCodes?.filter((zipCode: string) => zipCode !== id) || []
		}

		dispatch(setTargetedAreas(newTargetedAreas))

		// Update targeted areas via websocket
		if (gridDoId) {
			updateTargetedAreas(newTargetedAreas).catch(console.error)
		}
	}

	const handleUpdateGridFilter = (filterKey: string, filterValue: number) => {
		dispatch(commitTargetingFilter({ id: filterKey, percentage: filterValue }))
	}

	const handleUpdateTargetingFilter = (filterKey: string, filterValue: number) => {
		dispatch(updateTargetingFilter({ id: filterKey, percentage: filterValue }))
	}

	const handleTargetingFiltersFromModal = (selectedKeys: string[]) => {
		if (!grid || !version) return

		const newTargetingFilters = selectedKeys.reduce(
			(acc, key) => {
				const referenceField = key as ReferenceFields
				// If the key is already in targeting filters, add the existing value to the new object
				if (version.targeting_filters[referenceField]) {
					acc[referenceField] = version.targeting_filters[referenceField]
					return acc
					// otherwise add a new object with a percentage of 0 (default state for the targeting filter)
				} else {
					acc[referenceField] = { percentage: 0 }
				}
				return acc
			},
			{} as typeof version.targeting_filters
		)
		dispatch(setTargetingFilters(newTargetingFilters))
	}

	const renderTargetingFilters = () => {
		try {
			return Object.entries(targetingFiltersState)
				.sort((a, b) => {
					return enabledReferenceFields.indexOf(a[0] as ReferenceFields) >
						enabledReferenceFields.indexOf(b[0] as ReferenceFields)
						? 1
						: -1
				})
				.map((entry) => {
					const key = entry[0] as keyof typeof referenceFieldStringMap
					const filter = entry[1] as { percentage: number }

					// Get friendly name for custom fields or fallback to built-in mapping
					const displayTitle =
						customFieldMappings[key]?.friendlyName || referenceFieldStringMap[key] || key

					return (
						<CardWithSlider
							key={key}
							title={displayTitle}
							pendingValue={filter.percentage}
							selectionValue={version?.targeting_filters[key]?.percentage || 0}
							onChange={(value) => handleUpdateTargetingFilter(key, value)}
							onRemove={() => handleRemoveTargetingFilter(key)}
							onUpdateSelection={(value) => {
								handleUpdateGridFilter(key, value)
							}}
							onCardClick={() => {
								if (selectedReferenceField !== key) {
									dispatch(setSelectedReferenceField(key))
								} else {
									dispatch(setSelectedReferenceField(ReferenceFields.Households))
								}
							}}
							selected={selectedReferenceField === key}
							field={key}
						/>
					)
				})
		} catch (error: any) {
			console.error(error)
		}
	}

	// Initialize selected version to the active version when versions load
	useEffect(() => {
		if (versions.length > 0 && !selectedVersionId) {
			const activeVersion = versions.find((v) => v.status === 'active')
			if (activeVersion) {
				const {
					uuid,
					grid_id: _grid_id,
					version_number: _version_number,
					created_at: _created_at,
					updated_at: _updated_at,
					...versionPayload
				} = activeVersion
				dispatch(
					setSelectedVersion({
						versionId: uuid,
						version: versionPayload,
					})
				)
			}
		}
	}, [versions, selectedVersionId, dispatch])

	const handleEditTargetedArea = (
		oldType: string,
		oldId: WorkspaceLocation['uuid'] | County['id'] | string,
		targetedArea: {
			type: TargetingType
			data: {
				locationId?: WorkspaceLocation['uuid']
				radius?: number
				county?: County['id']
				zipCode?: string
			}
		}
	) => {
		if (!grid || !version) return

		const newTargetedAreas = { ...version.targeted_areas }

		// First remove the old targeted area
		if (oldType === 'radius') {
			newTargetedAreas.coordinates =
				newTargetedAreas.coordinates?.filter(
					(coord: { locationId: WorkspaceLocation['uuid'] }) => coord.locationId !== oldId
				) || []
		} else if (oldType === 'county') {
			newTargetedAreas.counties =
				newTargetedAreas.counties?.filter((countyId: County['id']) => countyId !== oldId) || []
		} else if (oldType === 'zipcode') {
			newTargetedAreas.zipCodes =
				newTargetedAreas.zipCodes?.filter((zipCode: string) => zipCode !== oldId.toString()) || []
		}

		// Then add the new targeted area
		if (targetedArea.type === 'radius' && targetedArea.data.locationId) {
			const location = locations?.find((l) => l.uuid === targetedArea.data.locationId)
			if (location) {
				newTargetedAreas.coordinates = [
					...(newTargetedAreas.coordinates || []),
					{
						lat: location.latitude,
						lng: location.longitude,
						radius: targetedArea.data.radius || DEFAULT_RADIUS_VALUE,
						locationId: location.uuid,
					},
				]
			}
		} else if (targetedArea.type === 'county' && targetedArea.data.county) {
			newTargetedAreas.counties = [...(newTargetedAreas.counties || []), targetedArea.data.county]
		} else if (targetedArea.type === 'zip code' && targetedArea.data.zipCode) {
			newTargetedAreas.zipCodes = [...(newTargetedAreas.zipCodes || []), targetedArea.data.zipCode]
		}

		dispatch(setTargetedAreas(newTargetedAreas))

		// Update targeted areas via websocket
		if (gridDoId) {
			updateTargetedAreas(newTargetedAreas).catch(console.error)
		}
	}

	return (
		<div className="w-full min-w-0">
			<div className="flex flex-col w-full min-w-0">
				{/* Grid Details Section */}
				<div className="mb-6">
					<div className="flex items-center justify-between h-10 mb-3">
						<h3 className="text-base font-semibold uppercase">GRID DETAILS</h3>
					</div>
					<div className="flex flex-col space-y-3">
						<div className="flex gap-3 w-full">
							<div className="flex-1 min-w-0">
								<InputField
									label="Grid Name"
									placeholder="Grid name"
									required
									className="w-full"
									onChange={(event) => {
										const inputValue = event.target.value
										if (inputValue.length <= NAME_CHARACTER_LIMIT - 1) {
											dispatch(setGrid({ ...grid, name: inputValue }))
										}
									}}
									value={grid.name}
								/>
							</div>
							{/* Version takes 1/3 of the line */}
							{versions.length > 0 && (
								<div className="w-1/3 min-w-0">
									<div className="space-y-2">
										<Label>Version</Label>
										<div className="space-y-1">
											<Select
												value={
													selectedVersionId ||
													versions.find((v) => v.status === 'active')?.uuid ||
													''
												}
												onValueChange={(versionId) => {
													const selectedVersion = versions.find((v) => v.uuid === versionId)
													if (selectedVersion) {
														const {
															id: _id,
															grid_id: _grid_id,
															version_number: _version_number,
															created_at: _created_at,
															updated_at: _updated_at,
															...versionPayload
														} = selectedVersion
														dispatch(
															setSelectedVersion({
																versionId,
																version: versionPayload,
															})
														)
													}
												}}
											>
												<SelectTrigger className="w-full">
													<SelectValue placeholder="Select version">
														{(() => {
															const selectedVersion = versions.find(
																(v) => v.uuid === selectedVersionId
															)
															return selectedVersion
																? selectedVersion.name || `v${selectedVersion.version_number}`
																: 'Select version'
														})()}
													</SelectValue>
												</SelectTrigger>
												<SelectContent>
													{versions.map((version) => (
														<SelectItem key={version.uuid} value={version.uuid}>
															<div className="flex items-center justify-between w-full">
																<span>{version.name || `v${version.version_number}`}</span>
																{version.status === 'active' && (
																	<Badge variant="default" className="ml-2">
																		Active
																	</Badge>
																)}
															</div>
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											{/* Reserved space for sub-text - maintains consistent height with other inputs */}
											<div className="h-5 flex items-start">
												{/* No sub-text for Version field currently */}
											</div>
										</div>
									</div>
								</div>
							)}
						</div>
					</div>

					{/* Target Areas Section */}
					<GridTargetedAreasSection
						version={version}
						locations={locations}
						counties={counties}
						zipCodes={zipCodes}
						totalHouseholdCount={totalHouseholdCount}
						setIsAddTargetedAreaModalOpen={setIsAddTargetedAreaModalOpen}
						removeTargetedArea={removeTargetedArea}
						handleEditTargetedArea={handleEditTargetedArea}
					/>

					{/* Filters Section */}
					<div className="mb-6">
						<div className="flex items-center justify-between h-10 mb-3">
							<h3 className="text-base font-semibold uppercase">FILTERS</h3>
							<Button
								variant="ghost"
								size="icon"
								className="cursor-pointer"
								onClick={handleAddFilterButton}
							>
								<Plus className="h-4 w-4" />
							</Button>
						</div>
						<div className="flex flex-col space-y-3 w-full" data-testid="targeting-filters">
							{hasTargetingFilters || hasInclusionFilters ? (
								<GridDataDisplay
									main={coverage.toLocaleString('en-US', {
										style: 'percent',
										maximumFractionDigits: 1,
									})}
									bottom={`${targetedHouseholdCount.toLocaleString('en-US', {
										maximumFractionDigits: 0,
									})} of ${totalHouseholdCount.toLocaleString('en-US', {
										maximumFractionDigits: 0,
									})} population targeted`}
								/>
							) : null}
							{hasInclusionFilters ? <ManualFilterCard type="inclusion" /> : null}
							{hasExclusionFilters ? <ManualFilterCard type="exclusion" /> : null}
							{hasTargetingFilters ? renderTargetingFilters() : null}
							{!hasInclusionFilters && !hasExclusionFilters && !hasTargetingFilters ? (
								<div className="mt-2">
									<p className="text-sm text-muted-foreground">No filters added</p>
								</div>
							) : null}
						</div>
					</div>
				</div>
			</div>
			<AddTargetedAreaModal
				open={isAddTargetedAreaModalOpen}
				onClose={() => setIsAddTargetedAreaModalOpen(false)}
				onSave={handleAddTargetedArea}
				locations={locations}
				counties={counties}
				zipCodes={zipCodes}
			/>
			<TargetingFilterModal
				open={isTargetingFilterModalOpen}
				onSave={(selectedKeys) => {
					handleTargetingFiltersFromModal(selectedKeys)
					setIsTargetingFilterModalOpen(false)
				}}
				onClose={() => {
					setIsTargetingFilterModalOpen(false)
				}}
				targetingFilters={targetingFiltersState}
				customDataKeys={customFieldKeys}
				fieldMappings={customFieldMappings}
			/>
		</div>
	)
}
